import { useMemo, useState } from 'react';
import { Grid, TextField, MenuItem, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { createAutojob2, CreateAutojob2Request } from 'src/shared/services/autojobService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';

type CreateAutojobModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const assemblyNameOptions = [
  { value: 'CreateAndSendAutogiroInvoices', label: 'CreateAndSendAutogiroInvoices' },
  { value: 'CreateSwishMemberInvoices', label: 'CreateSwishMemberInvoices' },
  { value: 'ChargeSwishMemberInvoices', label: 'ChargeSwishMemberInvoices' },
  { value: 'SendEmailRemindersForAutogiromandateDonor', label: 'SendEmailRemindersForAutogiromandateDonor' },
];

// Jobb som bara Föreningssupport själva kör – tasken går igenom samtliga kunder (cross-tenant),
// så den ska bara läggas upp en gång, av oss, på custName 'fsupport'.
const FSUPPORT_CUST_NAME = 'fsupport';

const fsupportOnlyAssemblyNameOptions = [{ value: 'RemindSendingOrder', label: 'RemindSendingOrder' }];

// Beskrivning per assembly som visas i dialogen – så den som lägger upp jobbet vet vad som krävs i övrigt och hur det funkar.
const assemblyDescriptions: Record<string, string> = {
  CreateAndSendAutogiroInvoices: 'Skapar och skickar autogiroavisering för medlemsavgift till Bankgirot.',
  CreateSwishMemberInvoices: 'Skapar medlemsavier för Swish Återkommande.',
  ChargeSwishMemberInvoices: 'Gör Swish-dragning för medlemsavgift på redan skapade avier.',
  SendEmailRemindersForAutogiromandateDonor:
    'Skickar påminnelse-mail till månadsgivare (gåva) vars autogiromedgivande ännu inte är signerat.\n\n' +
    'Förutsättning: Aktivera "Skicka påminnelser via e-post" på insamlingen och redigera texterna för Påminnelse 1 och 2 där (insamlingens inställningar). Är det avstängt skickas inget.\n\n' +
    'Urval: Osignerade medgivanden. Påminnelse 1 skickas till medgivanden skapade det senaste dygnet, Påminnelse 2 när medgivandet är äldre än 3 dygn – därefter inget mer. Länken i mailet leder till rätt steg (välj bank/konto om bankkonto saknas, annars signera).\n\n' +
    'Körs dagligen (t.ex. kl 13). Varje utskick loggas på medgivandet.',
  RemindSendingOrder:
    'Påminner beställaren om beställningar av fakturautskick som inte är kompletta.\n\n' +
    'OBS: körs bara av Föreningssupport – tasken går själv igenom samtliga kunder i Fakturamodulen, ' +
    'oavsett vilken kund jobbet ligger på. Lägg därför bara upp den EN gång (här, på fsupport).\n\n' +
    'Urval: beställningar med status Inskickad eller Komplettering behövs, där texter saknas ELLER där Kivra ' +
    'är vald som leveransmetod men kundens Kivra-nyckel inte är aktiverad.\n\n' +
    'Kadens: Påminnelse 1 skickas 12 dygn före utskicksdatumet, Påminnelse 2 vid 8 dygn – därefter inget mer. ' +
    'Produktionsdeadline som visas i mailet är utskicksdatum minus 8 dygn. Max ett mail per order och körning.\n\n' +
    'Körs dagligen. Mailet skickas från Föreningssupport (noreply) till beställarens e-postadress.',
};

const scheduleTypeOptions = [
  { value: 0, labelKey: 'settings:autojobs.scheduleTypes.once' },
  { value: 1, labelKey: 'settings:autojobs.scheduleTypes.minute' },
  { value: 2, labelKey: 'settings:autojobs.scheduleTypes.hourly' },
  { value: 3, labelKey: 'settings:autojobs.scheduleTypes.daily' },
  { value: 4, labelKey: 'settings:autojobs.scheduleTypes.weekly' },
  { value: 5, labelKey: 'settings:autojobs.scheduleTypes.monthly' },
  { value: 6, labelKey: 'settings:autojobs.scheduleTypes.yearly' },
];

const defaultForm: CreateAutojob2Request = {
  taskName: '',
  assemblyName: '',
  scheduleType: 0,
  increaseNum: 0,
  startTimeStamp: new Date(Date.now() + 2 * 60 * 1000).toISOString().slice(0, 16),
  custom1: '',
  custom2: '',
  custom3: '',
  custom4: '',
  custom5: '',
  custom6: '',
  custom7: '',
  custom8: '',
  custom9: '',
  custom10: '',
  custom11: '',
  custom12: '',
};

export const CreateAutojobModal = (props: CreateAutojobModalProps) => {
  const { open, onClose, onSaved } = props;
  const { t } = useTranslation(['settings', 'common']);
  const { currentUser } = useGlobals();
  const [form, setForm] = useState<CreateAutojob2Request>({ ...defaultForm });
  const [saving, setSaving] = useState(false);

  // Fsupport-egna jobb visas bara när vi är inloggade på vårt eget register.
  const availableAssemblyNameOptions = useMemo(() => {
    const isFsupport = currentUser?.custName?.toLowerCase() === FSUPPORT_CUST_NAME;
    return isFsupport ? [...assemblyNameOptions, ...fsupportOnlyAssemblyNameOptions] : assemblyNameOptions;
  }, [currentUser?.custName]);

  const updateField = (field: keyof CreateAutojob2Request, value: string | number) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      // Förifyll fält vid val av autogiro
      if (field === 'assemblyName' && value === 'CreateAndSendAutogiroInvoices') {
        updated.taskName = updated.taskName || 'Autogiroavisering medlemsavgift';
        updated.scheduleType = 5;
        updated.increaseNum = 1;
        updated.custom1 = 'CreateInvoices';
      }

      // Förifyll fält vid val av Swish Återkommande — båda körs dagligen (schematyp 3, intervall 1)
      if (field === 'assemblyName' && value === 'CreateSwishMemberInvoices') {
        updated.taskName = updated.taskName || 'Medlemsavisering, Swish Återkommande';
        updated.scheduleType = 3;
        updated.increaseNum = 1;
      }

      if (field === 'assemblyName' && value === 'ChargeSwishMemberInvoices') {
        updated.taskName = updated.taskName || 'Swishdragning, medlemsavgift';
        updated.scheduleType = 3;
        updated.increaseNum = 1;
      }

      // Förifyll fält vid val av autogiro-påminnelser (månadsgivare) — körs dagligen
      if (field === 'assemblyName' && value === 'SendEmailRemindersForAutogiromandateDonor') {
        updated.taskName = updated.taskName || 'Påminnelse autogiromedgivande (månadsgivare)';
        updated.scheduleType = 3;
        updated.increaseNum = 1;
      }

      // Förifyll fält vid val av påminnelser för fakturautskicksbeställningar — körs dagligen
      if (field === 'assemblyName' && value === 'RemindSendingOrder') {
        updated.taskName = updated.taskName || 'Påminnelse beställning fakturautskick';
        updated.scheduleType = 3;
        updated.increaseNum = 1;
      }

      return updated;
    });
  };

  const handleSave = async () => {
    if (!form.taskName || !form.assemblyName) return;
    setSaving(true);
    try {
      await createAutojob2(form);
      toast.success(<>{t('common:statusMessages.saveChangesSuccess')}</>);
      setForm({ ...defaultForm });
      onSaved();
    } catch {
      toast.error(<>{t('common:statusMessages.saveChangesError')}</>);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({ ...defaultForm });
    onClose();
  };

  const customFields = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <GenericDialog
      open={open}
      dialogTitle={t('settings:autojobs.createModal.title')}
      maxWidth='sm'
      onClose={handleClose}
      onSave={handleSave}
      saveLabel='common:button.save'
      abortLabel='common:button.cancel'
      dialogContent={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.name')}
              value={form.taskName}
              onChange={(e) => updateField('taskName', e.target.value)}
              fullWidth
              size='small'
              required
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Assembly'
              value={form.assemblyName}
              onChange={(e) => updateField('assemblyName', e.target.value)}
              fullWidth
              size='small'
              required
              select
              disabled={saving}
            >
              {availableAssemblyNameOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {assemblyDescriptions[form.assemblyName] && (
            <Grid item xs={12}>
              <Alert severity='info' sx={{ whiteSpace: 'pre-line' }}>
                {assemblyDescriptions[form.assemblyName]}
              </Alert>
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.scheduleType')}
              value={form.scheduleType}
              onChange={(e) => updateField('scheduleType', Number(e.target.value))}
              fullWidth
              size='small'
              select
              disabled={saving}
            >
              {scheduleTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('dashboard:autojobs.interval')}
              value={form.increaseNum}
              onChange={(e) => updateField('increaseNum', Number(e.target.value))}
              fullWidth
              size='small'
              type='number'
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.createModal.startTime')}
              value={form.startTimeStamp}
              onChange={(e) => updateField('startTimeStamp', e.target.value)}
              fullWidth
              size='small'
              type='datetime-local'
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
          </Grid>
          {customFields.map((num) => {
            const key = `custom${num}` as keyof CreateAutojob2Request;
            return (
              <Grid item xs={6} key={key}>
                <TextField
                  label={`Custom ${num}`}
                  value={form[key]}
                  onChange={(e) => updateField(key, e.target.value)}
                  fullWidth
                  size='small'
                  disabled={saving}
                />
              </Grid>
            );
          })}
        </Grid>
      }
    />
  );
};
